import { logger } from '../services/logger';
import { monitoring } from '../services/monitoring';

interface CircuitBreakerOptions {
  maxFailures?: number;
  halfOpenAfterMs?: number;
  onStateChange?: (state: CircuitState) => void;
}

type CircuitState = 'CLOSED' | 'OPEN' | 'HALF_OPEN';

class CircuitBreaker {
  private failures = 0;
  private lastFailureTime = 0;
  private state: CircuitState = 'CLOSED';
  private readonly maxFailures: number;
  private readonly halfOpenAfterMs: number;
  private readonly onStateChange?: (state: CircuitState) => void;

  constructor(options: CircuitBreakerOptions = {}) {
    this.maxFailures = options.maxFailures || 5;
    this.halfOpenAfterMs = options.halfOpenAfterMs || 30000; // 30 seconds
    this.onStateChange = options.onStateChange;
  }

  async execute<T>(operation: () => Promise<T>): Promise<T> {
    if (this.state === 'OPEN') {
      const timeSinceLastFailure = Date.now() - this.lastFailureTime;
      if (timeSinceLastFailure >= this.halfOpenAfterMs) {
        this.transitionTo('HALF_OPEN');
      } else {
        throw new Error('Circuit breaker is OPEN');
      }
    }

    try {
      const result = await operation();
      this.onSuccess();
      return result;
    } catch (error) {
      this.onFailure(error);
      throw error;
    }
  }

  private onSuccess() {
    if (this.state !== 'CLOSED') {
      this.transitionTo('CLOSED');
    }
    this.failures = 0;
  }

  private onFailure(error: any) {
    this.failures++;
    this.lastFailureTime = Date.now();

    if (this.failures >= this.maxFailures) {
      this.transitionTo('OPEN');
      monitoring.reportError(error, {
        context: 'circuit-breaker',
        failures: this.failures,
        lastFailure: this.lastFailureTime
      });
    }
  }

  private transitionTo(newState: CircuitState) {
    if (this.state !== newState) {
      this.state = newState;
      logger.info('Circuit breaker state change:', {
        from: this.state,
        to: newState
      });
      this.onStateChange?.(newState);
    }
  }

  public getState(): CircuitState {
    return this.state;
  }

  public reset() {
    this.failures = 0;
    this.lastFailureTime = 0;
    this.transitionTo('CLOSED');
  }
}

// Create circuit breakers for each external service
export const circuitBreakers = {
  openai: new CircuitBreaker({
    maxFailures: 3,
    halfOpenAfterMs: 15000, // 15 seconds
    onStateChange: (state) => {
      logger.warn('OpenAI circuit breaker state change:', { state });
    }
  }),
  stripe: new CircuitBreaker({
    onStateChange: (state) => {
      logger.warn('Stripe circuit breaker state change:', { state });
    }
  }),
  supabase: new CircuitBreaker({
    onStateChange: (state) => {
      logger.warn('Supabase circuit breaker state change:', { state });
    }
  })
};